import type { UseFormRegister, FieldErrors } from 'react-hook-form';
import type { FormSubmissionInput } from '@qdf/shared';
import { OPERATING_MARKETS } from '@qdf/shared';
import { Input } from '../ui/Input.js';
import { Select } from '../ui/Select.js';

interface CreditRequestSectionProps {
  register: UseFormRegister<FormSubmissionInput>;
  errors: FieldErrors<FormSubmissionInput>;
  /** Optional footnote under the fields. Empty/absent = hidden. */
  pilotNote?: string;
  /** Options for the Markets Operating select. Defaults to the Left Main list. */
  markets?: readonly { value: string; label: string }[];
}

const defaultMarketOptions = OPERATING_MARKETS.map((m) => ({ value: m.value, label: m.label }));

export function CreditRequestSection({
  register,
  errors,
  pilotNote = '',
  markets,
}: CreditRequestSectionProps) {
  const marketOptions = markets
    ? markets.map((m) => ({ value: m.value, label: m.label }))
    : defaultMarketOptions;
  return (
    <fieldset>
      <legend className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
        Credit Request
      </legend>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Line of Credit Amount Requested"
          type="number"
          required
          placeholder="$0"
          {...register('locAmountRequested', { valueAsNumber: true })}
          error={errors.locAmountRequested?.message}
        />

        <Select
          label="Markets Operating"
          required
          options={marketOptions}
          {...register('marketsOperating')}
          error={errors.marketsOperating?.message}
        />

        <Input
          label="Avg. Annual Transactions"
          type="number"
          required
          placeholder="e.g. 25"
          {...register('avgAnnualTransactions', { valueAsNumber: true })}
          error={errors.avgAnnualTransactions?.message}
        />

        <Input
          label="Avg. Purchase Price"
          type="number"
          required
          placeholder="$0"
          {...register('avgPurchasePrice', { valueAsNumber: true })}
          error={errors.avgPurchasePrice?.message}
        />

        <Input
          label="Preferred Title Company"
          {...register('preferredTitleCompany')}
          error={errors.preferredTitleCompany?.message}
          className="sm:col-span-2"
        />
      </div>

      {pilotNote && (
        <p className="mt-3 text-xs text-gray-500">
          {pilotNote}
        </p>
      )}
    </fieldset>
  );
}
